/**
 * 颜色对比度小工具（WCAG 相对亮度 / 对比度），给文件卡片挑一个在当前气泡上看得清的强调色。
 * 只认 #rgb / #rrggbb / #rrggbbaa 与 rgb() / rgba()，小部件配色面板出来的都是这几种；
 * 解析不了（渐变、关键字、CSS 变量）返回 null，调用方按"无法判断"处理。
 */

export type Rgb = [number, number, number];

export const parseColor = (input?: string | null): Rgb | null => {
  const value = input?.trim();
  if (!value) return null;

  const hex = value.match(/^#([0-9a-f]{3,8})$/i)?.[1];
  if (hex) {
    if (hex.length === 3 || hex.length === 4) {
      return [0, 1, 2].map((i) => parseInt(hex[i] + hex[i], 16)) as Rgb;
    }
    if (hex.length === 6 || hex.length === 8) {
      return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb;
    }
    return null;
  }

  const rgb = value.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (rgb) {
    const channels = [rgb[1], rgb[2], rgb[3]].map(Number) as Rgb;
    return channels.every((c) => c <= 255) ? channels : null;
  }

  return null;
};

const channelLuminance = (channel: number) => {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

export const relativeLuminance = ([r, g, b]: Rgb) =>
  0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);

/** 强调色在底色上至少要有的对比度（WCAG 对 UI 组件 / 大字的 3:1 门槛） */
export const MIN_ACCENT_CONTRAST = 3;

/**
 * 给回答里的文件卡片挑强调色：
 * - 有品牌主色（accentColor）：用它；但已知气泡色且在上面看不清（< 3:1）→ 跟正文文字色（currentColor）
 * - 没有品牌主色、正文文字色被自定义：跟文字色（用户已保证它在气泡上可读）
 * - 都没改：用主题主色；已知气泡色且主题色在上面看不清 → 跟文字色
 * 返回 undefined 表示走主题主色（CSS 变量），返回 'currentColor' 表示跟正文文字色。
 */
export const pickReadableAccent = ({
  accentColor,
  textColor,
  bubbleColor,
  themeColor,
}: {
  accentColor?: string;
  textColor?: string;
  bubbleColor?: string;
  themeColor?: string;
}): string | undefined => {
  const tooLowOn = (color?: string) => {
    const ratio = contrastRatio(color, bubbleColor);
    return ratio !== null && ratio < MIN_ACCENT_CONTRAST;
  };

  const accent = accentColor?.trim();
  if (accent) return tooLowOn(accent) ? 'currentColor' : accent;
  if (textColor?.trim()) return 'currentColor';
  return tooLowOn(themeColor) ? 'currentColor' : undefined;
};

/** WCAG 对比度 1–21；任一颜色解析不了返回 null */
export const contrastRatio = (a?: string | null, b?: string | null): number | null => {
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return null;
  const la = relativeLuminance(ca);
  const lb = relativeLuminance(cb);
  const [light, dark] = la >= lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
};
